/**
 * QuickAddGrid — 8-tile category picker, used in two modes:
 *
 *   - `mode="navigation"` (the drawer detail page) — tiles are `<Link>`s
 *     that deep-link into the voucher composer with `?category=<slug>`.
 *   - `mode="select"` (the composer) — tiles are buttons that call
 *     `onSelect(slug)`, with the active tile highlighted in sky.
 *
 * The 8 tiles map 1:1 to the Indian SME Petty Cash Book columns (postage,
 * conveyance, travel, refreshments, office supplies, printing,
 * repairs, sundries) — closed for V1 per ADR-0012 §10 Q7.
 *
 * Each tile shows the category icon (from `lib/categories.tsx`), name,
 * and this-month total. `—` is rendered for zero-spend categories so the
 * grid stays visually quiet for unused heads.
 *
 * @see prototype/cash/petty-cash-expense.html `.cat-grid` (lines ~1054–1166)
 */
import { Link } from "@tanstack/react-router";
import { cn } from "@flint/ui";
import { CATEGORY_META, type PettyCashCategory } from "../lib/categories.tsx";
import { paiseToInr } from "../../../lib/format.ts";

// Phase 1c will register `/cash/petty-cash/new`; cast widens to string.
const ROUTE_NEW = "/cash/petty-cash/new" as string;

export interface QuickAddGridCategory {
  slug: PettyCashCategory;
  /**
   * This-month spend total for this category, paise (stringified bigint).
   * `"0"` renders as a dashed empty-state amount.
   */
  totalPaise: string;
}

interface BaseProps {
  /** Eight rows expected — one per category (caller's data shape). */
  categories: readonly QuickAddGridCategory[];
}

interface NavigationProps extends BaseProps {
  mode: "navigation";
  onSelect?: never;
  selected?: never;
}

interface SelectProps extends BaseProps {
  mode: "select";
  onSelect: (slug: PettyCashCategory) => void;
  /** Optional currently-selected slug, for highlight (only in `select` mode). */
  selected?: PettyCashCategory;
}

export type QuickAddGridProps = NavigationProps | SelectProps;

export function QuickAddGrid(props: QuickAddGridProps) {
  return (
    <div
      role={props.mode === "select" ? "radiogroup" : undefined}
      aria-label={props.mode === "select" ? "Voucher category" : undefined}
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
    >
      {props.categories.map((cat) => (
        <QuickAddTile
          key={cat.slug}
          slug={cat.slug}
          totalPaise={cat.totalPaise}
          mode={props.mode}
          selected={props.mode === "select" ? props.selected === cat.slug : false}
          onSelect={props.mode === "select" ? props.onSelect : undefined}
        />
      ))}
    </div>
  );
}

interface QuickAddTileProps {
  slug: PettyCashCategory;
  totalPaise: string;
  mode: "navigation" | "select";
  selected: boolean;
  onSelect: ((slug: PettyCashCategory) => void) | undefined;
}

function QuickAddTile({ slug, totalPaise, mode, selected, onSelect }: QuickAddTileProps) {
  const meta = CATEGORY_META[slug];
  const isEmpty = (totalPaise || "0") === "0";

  const tileClass = cn(
    "group flex flex-col gap-2 text-left p-3 rounded-md border bg-paper transition-colors",
    "hover:bg-raised hover:border-line-2",
    selected
      ? "bg-sky-50 border-sky-600 text-sky-700 hover:bg-sky-50 hover:border-sky-600"
      : "border-line text-ink",
  );

  const iconWrap = (
    <span
      aria-hidden
      className={cn(
        "inline-grid place-items-center w-7 h-7 rounded-md shrink-0",
        selected ? "bg-sky-100 text-sky-700" : "bg-bg text-muted",
      )}
    >
      <svg
        width={14}
        height={14}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {meta.icon}
      </svg>
    </span>
  );

  const inner = (
    <>
      <div className="flex items-center gap-2">
        {iconWrap}
        <span className="text-sm font-semibold truncate">{meta.label}</span>
      </div>
      <div className="text-xs text-muted">
        {isEmpty ? (
          <span className="text-line-2 font-medium">—</span>
        ) : (
          <>
            <span className="num text-ink-2 font-semibold">{paiseToInr(totalPaise)}</span>
            <span className="ml-1">/ mo</span>
          </>
        )}
      </div>
    </>
  );

  if (mode === "navigation") {
    return (
      <Link
        to={ROUTE_NEW}
        search={{ category: slug } as Record<string, string>}
        className={tileClass}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect?.(slug)}
      className={tileClass}
    >
      {inner}
    </button>
  );
}
